import { Version, useVersion } from "@/domain/Version";
import { useSetEffect } from "@/utils/useSetEffect";
import React, { createContext, ReactNode, useContext, useMemo } from "react";
import { useMatch } from "react-router";

const ctx = createContext<{
  version: Version | null;
  _setVersion: React.Dispatch<React.SetStateAction<Version | null>>;
}>({ version: null, _setVersion: () => {} });

const AppCurrentVersionProviderHelper: React.FC<{
  params: { organizationId: string; versionId: string };
}> = ({ params: { organizationId, versionId } }) => {
  const { _setVersion } = useContext(ctx);
  const [version] = useVersion(versionId, { organizationId });
  useSetEffect(() => {
    _setVersion(version);
  }, [version, _setVersion]);
  return null;
};

export const AppCurrentVersionProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const params = useMatch(
    "/organizations/:organizationId/versions/:versionId/*"
  )?.params;

  const [version, _setVersion] = React.useState<Version | null>(null);
  const value = useMemo(
    () => ({ version: params ? version : null, _setVersion }),
    [version, !!params]
  );

  return (
    <ctx.Provider value={value}>
      {params ? (
        <AppCurrentVersionProviderHelper
          params={params as { organizationId: string; versionId: string }}
        />
      ) : null}
      {children}
    </ctx.Provider>
  );
};

export const useAppCurrentVersion = () => {
  const { version } = useContext(ctx);
  return { version };
};
